import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  useMediaQuery,
  IconButton,
  CircularProgress,
  InputBase,
} from '@mui/material';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import RefreshIcon from '@mui/icons-material/Refresh';
import MainMenuDrawer from '../../components/Main/MenuDrawer';
import Header from '../../components/common/Header';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../../routes/paths';
import { getLatestNewsApi, getServiceLinksApi, getPesoExchangeRateApi } from '../../api';
import QuickLinkCard from '../../components/Main/QuickLinkCard';

const MainPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const [serviceLinks, setServiceLinks] = useState({
    youtube: '',
    telegram: '',
    kakao: '',
  });
  const [pesoRate, setPesoRate] = useState(null);
  const [pesoUpdatedAt, setPesoUpdatedAt] = useState(null);
  const [pesoLoading, setPesoLoading] = useState(false);
  const [pesoError, setPesoError] = useState('');
  const [phpAmount, setPhpAmount] = useState('1');
  const [krwAmount, setKrwAmount] = useState('');
  const [lastEditedCurrency, setLastEditedCurrency] = useState('PHP');
  const navigate = useNavigate();
  const isNarrow = useMediaQuery('(max-width:417px)');

  useEffect(() => {
    const loadLatestNews = async () => {
      try {
        const res = await getLatestNewsApi();
        const list = res?.data || [];

        const mapped = list.map((item) => ({
          id: item.newsId,
          type: item.headerName || '기타',
          title: item.title,
          color: item.color,
        }));

        setNewsItems(mapped);
      } catch (error) {
        console.error('Failed to load latest news:', error);
      } finally {
        // no-op
      }
    };

    loadLatestNews();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getServiceLinksApi();
        const items = res?.data || [];
        const next = { youtube: '', telegram: '', kakao: '' };
        for (const it of items) {
          if (it?.key === 'youtube' && it?.url) next.youtube = it.url;
          if (it?.key === 'telegram' && it?.url) next.telegram = it.url;
          if (it?.key === 'kakao' && it?.url) next.kakao = it.url;
        }
        if (alive) setServiceLinks(next);
      } catch {
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openExternal = (url) => {
    const u = String(url || '').trim();
    if (!u) {
      return;
    }
    window.open(u, '_blank', 'noopener,noreferrer');
  };

  const openYoutube = (url) => {
    const u = String(url || '').trim();
    if (!u) {
      return;
    }
    window.open(u, '_blank', 'noopener,noreferrer');
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  const handleEnterShops = () => {
    navigate(PATHS.shops);
  };

  const fetchPesoRate = useCallback(
    async (force = false) => {
      try {
        setPesoLoading(true);
        setPesoError('');
        const res = await getPesoExchangeRateApi(force ? { force: true } : {});
        const data = res?.data || res || {};
        if (typeof data.rate === 'number') {
          setPesoRate(data.rate);
        }
        setPesoError(
          data?.error ? `실시간 업데이트 실패, 마지막 데이터 표시 중 (${data.error})` : '',
        );
        setPesoUpdatedAt(data?.updatedAt || Date.now());
      } catch (error) {
        setPesoError(error.message || '환율 정보를 불러오지 못했습니다.');
      } finally {
        setPesoLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchPesoRate();
  }, [fetchPesoRate]);

  const formatUpdatedAt = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatUnitCaption = useCallback((value, unit, locale = 'ko-KR') => {
    const num = parseFloat(value);
    if (!Number.isFinite(num)) {
      return `0 ${unit}`;
    }
    const formatted = num.toLocaleString(locale, {
      maximumFractionDigits: 2,
    });
    return `${formatted} ${unit}`;
  }, []);

  const sanitizeNumericInput = useCallback((value) => value.replace(/[^0-9.]/g, ''), []);

  const formatCalculatedAmount = useCallback((num) => {
    if (!Number.isFinite(num)) return '';
    const rounded = Math.round(num * 100) / 100;
    return rounded.toString();
  }, []);

  const handlePhpInputChange = useCallback(
    (value) => {
      const numeric = sanitizeNumericInput(value);
      setLastEditedCurrency('PHP');
      setPhpAmount(numeric);
      if (!pesoRate || numeric === '') {
        setKrwAmount('0');
        return;
      }
      const numberValue = parseFloat(numeric);
      if (Number.isNaN(numberValue)) {
        setKrwAmount('0');
        return;
      }
      setKrwAmount(formatCalculatedAmount(numberValue * pesoRate));
    },
    [formatCalculatedAmount, pesoRate, sanitizeNumericInput],
  );

  const handleKrwInputChange = useCallback(
    (value) => {
      const numeric = sanitizeNumericInput(value);
      setLastEditedCurrency('KRW');
      setKrwAmount(numeric);
      if (!pesoRate || numeric === '') {
        setPhpAmount('0');
        return;
      }
      const numberValue = parseFloat(numeric);
      if (Number.isNaN(numberValue)) {
        setPhpAmount('0');
        return;
      }
      if (!pesoRate) {
        setPhpAmount('0');
        return;
      }
      setPhpAmount(formatCalculatedAmount(numberValue / pesoRate));
    },
    [formatCalculatedAmount, pesoRate, sanitizeNumericInput],
  );

  useEffect(() => {
    if (!pesoRate) return;
    if (lastEditedCurrency === 'PHP') {
      const numberValue = parseFloat(phpAmount);
      if (Number.isFinite(numberValue)) {
        setKrwAmount(formatCalculatedAmount(numberValue * pesoRate));
      } else {
        setKrwAmount('0');
      }
    } else {
      const numberValue = parseFloat(krwAmount);
      if (Number.isFinite(numberValue)) {
        setPhpAmount(formatCalculatedAmount(numberValue / pesoRate));
      } else {
        setPhpAmount('0');
      }
    }
  }, [formatCalculatedAmount, krwAmount, lastEditedCurrency, pesoRate, phpAmount]);

  return (
    <Box>
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          pb: 1,
        position: 'relative',
        }}
      >
        <MainMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          onNavigate={handleNavigate}
        />
        <Header
          variant="full"
          onClickMenu={() => setMenuOpen(true)}
        />

        <Box sx={{ mb: 4 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', mb: 1.5, cursor: 'pointer' }}
            onClick={() => navigate(PATHS.newsList)}
          >
            <Box
              component="img"
              src="/icon/chat_icon.png"
              alt="마닐라 소식 아이콘"
              sx={{ width: 18, height: 18, mr: 0.5 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 16 }}>
              마닐라 소식
            </Typography>
          </Box>
          <Stack spacing={1}>
            {newsItems.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                현재 등록된 마닐라 소식이 없습니다.
              </Typography>
            )}
            {newsItems.map((item) => (
              <Paper
                key={item.id}
                elevation={0}
                sx={{
                  px: 1.5,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  bgcolor: '#F7F7F8',
                  boxShadow: 'none',
                }}
                onClick={() => navigate(`/news/${item.id}`)}
              >
                <Chip
                  label={item.type}
                  size="small"
                  color="default"
                  sx={{
                    borderRadius: 1,
                    bgcolor: item.color || '#F0EFED',
                  }}
                />
                <Typography variant="body2" noWrap>
                  {item.title}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, fontSize:16 }}>
            주요 바로가기
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2, 
            }}
            >
              <QuickLinkCard
                titleLines={['E-TRAVEL / 픽드랍']}
                background="linear-gradient(135deg, #0D3C8C, #072E6D)"
                onClick={() => navigate(PATHS.etravelPickdrop)}
                rightIcon={(
                  <Box
                    component="img"
                    src="/icon/qr_icon.png"
                    alt="QR 아이콘"
                    sx={{ width: 35, height: 45 }}
                  />
                )}
              />

            <QuickLinkCard
              titleLines={['Manila701', '공식 유튜브']}
              background="linear-gradient(135deg, #165BB3, #0E489C)"
              onClick={() => openYoutube(serviceLinks.youtube)}
              rightIcon={(
                <Box
                  component="img"
                  src="/icon/youtube_icon.png"
                  alt="유튜브 아이콘"
                  sx={{ width: 35, height: 25 }}
                />
              )}
              iconGap={0.5}
            />
            <QuickLinkCard
              titleLines={['유흥/골프/식당']}
              subtitle="Entertainment"
              background="linear-gradient(135deg, #4848B7, #282F8D)"
              onClick={handleEnterShops}
              rightIcon={<LocalBarIcon sx={{ fontSize: 28 }} />}
            />
            <QuickLinkCard
              titleLines={['링크 모음']}
              subtitle="Link"
              background="#b0b0b0"
              onClick={() => navigate(PATHS.links)}
              rightIcon={(
                <Box
                  component="img"
                  src="/icon/link_icon.png"
                  alt="링크 모음 아이콘"
                  sx={{ width: 28, height: 28 }}
                />
              )}
            />
            <QuickLinkCard
              height={79}
              background="#007ACC"
              onClick={() => openExternal(serviceLinks.telegram)}
              leftIcon={(
                <Box
                  component="img"
                  src="/logo/tel__logo.png"
                  alt="텔레그램 로고"
                  sx={{ width: 38, height: 38, flexShrink: 0 }}
                />
              )}
              titleLines={isNarrow ? ['Manila701', '공식', '텔레그램'] : ['Manila701', '공식 텔레그램']}
            />
            <QuickLinkCard
              height={79}
              background="#ffcc00"
              onClick={() => openExternal(serviceLinks.kakao)}
              leftIcon={(
                <Box
                  component="img"
                  src="/logo/kakao_logo.png"
                  alt="카카오톡 로고"
                  sx={{ width: 38, height: 38, flexShrink: 0 }}
                />
              )}
              titleLines={isNarrow ? ['Manila701', '공식', '카카오톡'] : ['Manila701', '공식 카카오톡']}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 4, mb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              px: 2,
              py: 2,
              borderRadius: 3,
              border: '1px solid #e0e0e0',
              bgcolor: '#fff',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: 16 }}>
                페소 환율 계산
              </Typography>
              <IconButton
                size="small"
                onClick={() => fetchPesoRate(true)}
                disabled={pesoLoading}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #e5e7eb',
                  p: 0.4,
                  '&:hover': { bgcolor: '#fff' },
                }}
                aria-label="환율 새로고침"
              >
                {pesoLoading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gridTemplateRows: 'auto auto',
                    columnGap: 0.8,
                    rowGap: 0.2,
                    alignItems: 'center',
                    minWidth: 120,
                    px: 1.2,
                    py: 0.85,
                    bgcolor: '#f5f6f8',
                    borderRight: '1px solid #e5e7eb',
                    pt:1,
                    pb:1
                  }}
                >
                  <Box
                    component="img"
                    src="/image/Philippines_Flag.png"
                    alt="필리핀 국기"
                    sx={{
                      width: 24,
                      height: 16,
                      objectFit: 'cover',
                      borderRadius: '2px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      gridColumn: 1,
                      gridRow: 1,
                    }}
                  />
                  <Typography sx={{ fontSize: 13, color: '#111827', lineHeight: 1, gridColumn: 2, gridRow: 1 }}>
                    필리핀
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: 13,
                      lineHeight: 1,
                      color: '#6b7280',
                      gridColumn: 2,
                      gridRow: 2,
                    }}
                  >
                    PHP
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    px: 1.2
                  }}
                >
                  <InputBase
                    value={phpAmount}
                    onChange={(e) => handlePhpInputChange(e.target.value)}
                    inputProps={{
                      inputMode: 'decimal',
                      maxLength: 18,
                      style: {
                        textAlign: 'right',
                        fontSize: 16,
                        fontWeight: 700,
                        lineHeight: '22px',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    sx={{
                      width: '100%',
                      maxWidth: 200,
                      overflow: 'hidden',
                      '& input': {
                        width: '100%',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap', fontSize: 13, mt: '-10px' }}
                  >
                    {formatUnitCaption(phpAmount || '0', '페소', 'en-PH')}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  textAlign: 'center',
                  py: 0.3,
                }}
              >
                <Box
                  component="img"
                  src="/icon/equal_icon.png"
                  alt="equals"
                  sx={{ width: 20, height: 20, display: 'inline-block', mt:-2}}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  border: '1px solid #e5e7eb',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mt:-1.5
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr',
                    gridTemplateRows: 'auto auto',
                    columnGap: 0.8,
                    rowGap: 0.2,
                    alignItems: 'center',
                    minWidth: 120,
                    px: 1.2,
                    py: 0.85,
                    bgcolor: '#f5f6f8',
                    borderRight: '1px solid #e5e7eb',
                    pt:1,
                    pb:1
                  }}
                >
                  <Box
                    component="img"
                    src="/image/korea_Flag.png"
                    alt="대한민국 국기"
                    sx={{
                      width: 24,
                      height: 16,
                      objectFit: 'cover',
                      borderRadius: '2px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      gridColumn: 1,
                      gridRow: 1,
                    }}
                  />
                  <Typography sx={{ fontSize: 13, color: '#111827', lineHeight: 1, gridColumn: 2, gridRow: 1 }}>
                    대한민국
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      fontSize: 13,
                      lineHeight: 1,
                      color: '#6b7280',
                      gridColumn: 2,
                      gridRow: 2,
                    }}
                  >
                    KRW
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    px: 1.2,
                    gap: 0.05,
                  }}
                >
                  <InputBase
                    value={krwAmount}
                    onChange={(e) => handleKrwInputChange(e.target.value)}
                    inputProps={{
                      inputMode: 'decimal',
                      maxLength: 18,
                      style: {
                        textAlign: 'right',
                        fontSize: 16,
                        fontWeight: 700,
                        lineHeight: '22px',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    sx={{
                      width: '100%',
                      maxWidth: 200,
                      overflow: 'hidden',
                      '& input': {
                        width: '100%',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap', fontSize: 13, mt: '-10px' }}
                  >
                    {formatUnitCaption(krwAmount || '0', '원', 'ko-KR')}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary">
              {pesoUpdatedAt ? `${formatUpdatedAt(pesoUpdatedAt)} 기준` : '업데이트 정보 없음'}
            </Typography>
            {pesoError ? (
              <Typography variant="caption" color="error">
                {pesoError}
              </Typography>
            ) : null}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default MainPage;